import { useRoutes, Link, useLocation } from 'react-router-dom'
import { useAuth, signOut } from '@/hooks/useAuth'
import { useTheme } from '@/contexts/ThemeContext'
import { lumi } from '@/lib/lumi'
import {Sun, Moon, LogOut, Newspaper, List, Image, Users, Settings, ChevronRight, LayoutDashboard, Home, Loader2, Ban, Rocket, Instagram, MessageCircleDashed as MessageCircle} from 'lucide-react'
import toast from 'react-hot-toast'
import { Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'

import ProtectedRoute from '@/components/ProtectedRoute'
import BlockedUserOverlay from '@/components/BlockedUserOverlay'
import DashboardHome from '@/pages/admin/DashboardHome'
import NewArticle from '@/pages/admin/NewArticle'
import ListArticles from '@/pages/admin/ListArticles'
import ManageBanners from '@/pages/admin/ManageBanners'
import ManageUsers from '@/pages/admin/ManageUsers'
import SocialSettings from '@/pages/admin/SocialSettings'
import FooterSettings from '@/pages/admin/FooterSettings'
import BlockUsers from '@/pages/admin/BlockUsers'
import Booster from '@/pages/admin/Booster'
import InstagramAnalytics from '@/pages/admin/InstagramAnalytics'
import ModerateComments from '@/pages/admin/ModerateComments'
import type { SiteSetting } from '@/types'

// Rotas internas do admin — cada uma encapsulada com o nível correcto de permissão
const adminRoutes = [
  {
    path: '/',
    element: (
      <ProtectedRoute require="any">
        <DashboardHome />
      </ProtectedRoute>
    ),
  },
  {
    path: '/nova-noticia',
    element: (
      <ProtectedRoute require="any">
        <NewArticle />
      </ProtectedRoute>
    ),
  },
  {
    path: '/nova-noticia/:id',
    element: (
      <ProtectedRoute require="any">
        <NewArticle />
      </ProtectedRoute>
    ),
  },
  {
    path: '/noticias',
    element: (
      <ProtectedRoute require="any">
        <ListArticles />
      </ProtectedRoute>
    ),
  },
  {
    path: '/comentarios',
    element: (
      <ProtectedRoute require="any">
        <ModerateComments />
      </ProtectedRoute>
    ),
  },
  // Rotas sensíveis — exigem nível admin
  {
    path: '/banners',
    element: (
      <ProtectedRoute require="admin">
        <ManageBanners />
      </ProtectedRoute>
    ),
  },
  {
    path: '/usuarios',
    element: (
      <ProtectedRoute require="admin">
        <ManageUsers />
      </ProtectedRoute>
    ),
  },
  {
    path: '/bloquear-usuarios',
    element: (
      <ProtectedRoute require="admin">
        <BlockUsers />
      </ProtectedRoute>
    ),
  },
  {
    path: '/booster',
    element: (
      <ProtectedRoute require="admin">
        <Booster />
      </ProtectedRoute>
    ),
  },
  {
    path: '/meu-analytics',
    element: (
      <ProtectedRoute require="admin">
        <InstagramAnalytics />
      </ProtectedRoute>
    ),
  },
  {
    path: '/redes-sociais',
    element: (
      <ProtectedRoute require="admin">
        <SocialSettings />
      </ProtectedRoute>
    ),
  },
  {
    path: '/rodape',
    element: (
      <ProtectedRoute require="admin">
        <FooterSettings />
      </ProtectedRoute>
    ),
  },
]

// Itens do menu lateral
// adminOnly: false → visível para todos os utilizadores autenticados (admin, editor, autor)
// adminOnly: true  → visível apenas para administradores
const navItems = [
  { path: '/admin', label: 'Visão Geral', icon: LayoutDashboard, exact: true, adminOnly: false },
  { path: '/admin/nova-noticia', label: 'Nova Notícia', icon: Newspaper, adminOnly: false },
  { path: '/admin/noticias', label: 'Listar Notícias', icon: List, adminOnly: false },
  { path: '/admin/comentarios', label: 'Comentários', icon: MessageCircle, adminOnly: false },
  { path: '/admin/banners', label: 'Gerenciar Banners', icon: Image, adminOnly: true },
  { path: '/admin/usuarios', label: 'Usuários', icon: Users, adminOnly: true },
  { path: '/admin/bloquear-usuarios', label: 'MongoDB AUTH', icon: Ban, adminOnly: true },
  { path: '/admin/booster', label: 'Booster', icon: Rocket, adminOnly: true },
  { path: '/admin/meu-analytics', label: 'Meu Analytics', icon: Instagram, adminOnly: true },
  { path: '/admin/redes-sociais', label: 'Redes Sociais', icon: Settings, adminOnly: true },
  { path: '/admin/rodape', label: 'Rodapé & SEO', icon: Settings, adminOnly: true },
]

function AdminRoutes() {
  return useRoutes(adminRoutes)
}

export default function Dashboard() {
  const { isAuthenticated, isLoading, isAdmin, user } = useAuth()
  const location = useLocation()
  const { theme, toggleTheme } = useTheme()
  const [logoImage, setLogoImage] = useState<string>('')
  const [siteTitle, setSiteTitle] = useState('(Remix) carlos')

  useEffect(() => {
    Promise.all([
      lumi.entities.site_settings.list({ filter: { key: 'logo_image' }, limit: 1 }),
      lumi.entities.site_settings.list({ filter: { key: 'site_title' }, limit: 1 }),
    ]).then(([logoRes, titleRes]) => {
      if (logoRes.list[0]) setLogoImage((logoRes.list[0] as SiteSetting).value)
      if (titleRes.list[0]) setSiteTitle((titleRes.list[0] as SiteSetting).value)
    }).catch(() => {/* silencioso */})
  }, [])

  // Enquanto o estado de auth está a ser determinado, mostra ecrã de carregamento
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center transition-colors">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-red-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400">A verificar autenticação...</p>
        </div>
      </div>
    )
  }

  // Não autenticado — redireciona para login preservando a rota de destino
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />
  }

  const handleLogout = async () => {
    signOut()
    toast.success('Sessão encerrada com sucesso.')
  }

  // Filtra itens de navegação conforme nível de permissão
  const visibleNavItems = navItems.filter(item => !item.adminOnly || isAdmin)

  const isBlocked = user?.isBlocked === true && !isAdmin

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex transition-colors">
      {isBlocked && user ? <BlockedUserOverlay user={user} /> : null}
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col min-h-screen sticky top-0 h-screen overflow-y-auto">
        <div className="p-5 border-b border-gray-100 dark:border-gray-800">
          <Link to="/" className="flex items-center gap-2 mb-1">
            {logoImage ? (
              <img
                src={logoImage}
                alt={siteTitle}
                className="h-8 w-auto object-contain"
              />
            ) : (
              <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-black text-xs">RC</span>
              </div>
            )}
            <span className="font-bold text-gray-900 dark:text-white text-sm leading-tight truncate">
              {siteTitle}
            </span>
          </Link>
          <p className="text-xs text-gray-400 ml-10">Painel Administrativo</p>
        </div>

        {/* Nível de acesso */}
        {isAdmin ? (
          <div className="mx-3 mt-3 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
            <span className="text-xs font-semibold text-red-700 dark:text-red-400">Administrador</span>
          </div>
        ) : (
          <div className="mx-3 mt-3 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
            <span className="text-xs font-semibold text-blue-700 dark:text-blue-400">Editor / Autor</span>
          </div>
        )}

        <nav className="flex-1 py-4 px-3" aria-label="Menu administrativo">
          <ul className="space-y-1">
            {visibleNavItems.map(item => {
              const Icon = item.icon
              const isHomeActive = item.exact && location.pathname === '/admin'
              const isSubActive = !item.exact && location.pathname.startsWith(item.path)
              const active = isHomeActive || isSubActive

              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      active
                        ? 'bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon size={16} />
                    <span className="flex-1">{item.label}</span>
                    {active && <ChevronRight size={14} />}
                  </Link>
                </li>
              )
            })}
          </ul>

          {/* Separador visual para itens de admin ocultos */}
          {!isAdmin && (
            <div className="mt-4 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-dashed border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-400 leading-snug">
                Algumas opções estão restritas a administradores.
              </p>
            </div>
          )}
        </nav>

        {/* Acções inferiores */}
        <div className="p-3 border-t border-gray-100 dark:border-gray-800 space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
          >
            <Home size={16} />
            <span>Ver Site</span>
          </a>
          <button
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
            aria-label="Alternar tema"
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            <span>{theme === 'dark' ? 'Tema Claro' : 'Tema Escuro'}</span>
          </button>

          {/* Utilizador + logout */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-all">
            <div className="w-7 h-7 rounded-full bg-red-100 dark:bg-red-950 flex items-center justify-center flex-shrink-0">
              <span className="text-red-700 dark:text-red-400 text-xs font-bold">
                {user?.displayName?.charAt(0)?.toUpperCase() || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                {user?.displayName || 'Utilizador'}
              </p>
              <p className="text-xs text-gray-400 truncate">{user?.username || ''}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded"
              aria-label="Sair"
              title="Terminar sessão"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* Conteúdo principal */}
      <main className="flex-1 overflow-auto min-h-screen">
        <AdminRoutes />
      </main>
    </div>
  )
}
